import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { ProtectedRoute } from '@/layout/ProtectedRoute';
import { LoginPage } from '@/pages/LoginPage';
import { NoAccessPage } from '@/pages/NoAccessPage';
import { OverviewPage } from '@/pages/OverviewPage';
import { OidcProviderPage } from '@/pages/OidcProviderPage';
import { AuditLitePage } from '@/pages/AuditLitePage';
import { RuntimeConfigPage } from '@/pages/RuntimeConfigPage';
import { SkeletonRow } from '@/components/SkeletonRow';
import { FeishuEventsPage } from '@/pages/FeishuEventsPage';
import { FeishuEventDetailPage } from '@/pages/FeishuEventDetailPage';
import { LongConnStatusPage } from '@/pages/LongConnStatusPage';
import { GroupPollingRunsPage } from '@/pages/GroupPollingRunsPage';
import { BindingsPage } from '@/pages/BindingsPage';
import { PrincipalsPage } from '@/pages/PrincipalsPage';
import { MetricsDashboardPage } from '@/pages/MetricsDashboardPage';

/**
 * Manifest + Ingress pull in CodeMirror + the YAML parser. Lazy-load them so
 * they aren't included in the main bundle (saves ~600 KB pre-gzip).
 */
const AccessManifestPage = lazy(() =>
  import('@/pages/AccessManifestPage').then((m) => ({ default: m.AccessManifestPage })),
);
const IngressPlanPage = lazy(() =>
  import('@/pages/IngressPlanPage').then((m) => ({ default: m.IngressPlanPage })),
);

function LazyFallback() {
  return (
    <div className="rounded border border-outline-variant bg-surface p-container-padding">
      <SkeletonRow count={6} cells={4} />
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/no-access" element={<NoAccessPage />} />
      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<OverviewPage />} />
        <Route
          path="/manifest"
          element={
            <Suspense fallback={<LazyFallback />}>
              <AccessManifestPage />
            </Suspense>
          }
        />
        <Route
          path="/ingress"
          element={
            <Suspense fallback={<LazyFallback />}>
              <IngressPlanPage />
            </Suspense>
          }
        />
        <Route path="/oidc" element={<OidcProviderPage />} />
        <Route path="/audit" element={<AuditLitePage />} />
        <Route path="/runtime" element={<RuntimeConfigPage />} />
        <Route path="/ops/feishu-events" element={<FeishuEventsPage />} />
        <Route path="/ops/feishu-events/:eventId" element={<FeishuEventDetailPage />} />
        <Route path="/ops/longconn" element={<LongConnStatusPage />} />
        <Route path="/ops/group-polling" element={<GroupPollingRunsPage />} />
        <Route path="/ops/bindings" element={<BindingsPage />} />
        <Route path="/ops/principals" element={<PrincipalsPage />} />
        <Route path="/ops/metrics" element={<MetricsDashboardPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
